import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TopNav from '../components/shell/TopNav.jsx';
import FilmWall, { FilmGrid } from '../components/videography/FilmWall.jsx';
import FilmCard from '../components/videography/FilmCard.jsx';
import { useReducedMotion } from '../hooks/useReducedMotion.js';
import { formatRuntime } from '../utils/filmWall.js';
import FILMS from '../data/films.json';
import styles from './Videography.module.css';

// ─── Videography ──────────────────────────────────────────────────────────────
// A wall of every film. Click one and its card pops out of the wall.
//
// Films, their order and their card colours live in src/data/films.json —
// `npm run films` builds the posters, stills and hover loops for new entries.
// The previous cinema-screen + filmstrip layout is in git history (before
// "Videography: project-card wall").
// ─────────────────────────────────────────────────────────────────────────────

const FILTERS = [
  { key: 'all',      label: 'All',        test: () => true },
  { key: 'wide',     label: 'Widescreen', test: (f) => f.width >= f.height },
  { key: 'vertical', label: 'Vertical',   test: (f) => f.width < f.height },
];

function shuffle(list) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const filmFromUrl = () => {
  const id = new URLSearchParams(window.location.search).get('film');
  return FILMS.find((f) => f.id === id) ?? null;
};

function setFilmInUrl(id) {
  const url = new URL(window.location.href);
  if (id) url.searchParams.set('film', id); else url.searchParams.delete('film');
  window.history.replaceState(window.history.state, '', url);
}

function useCompact() {
  const query = '(max-width: 768px), (pointer: coarse)';
  const [compact, setCompact] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setCompact(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return compact;
}

const IconShuffle = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" />
    <polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" />
  </svg>
);

export default function Videography() {
  const reduced = useReducedMotion();
  const compact = useCompact();
  const flat = compact || reduced;

  const [filter, setFilter] = useState('all');
  const [order, setOrder]   = useState(FILMS);
  const [hovered, setHovered] = useState(null);
  const [openId, setOpenId] = useState(() => filmFromUrl()?.id ?? null);
  const [wallKey, setWallKey] = useState(0);
  const openerRef = useRef(null);

  const films = useMemo(
    () => order.filter(FILTERS.find((f) => f.key === filter).test),
    [order, filter],
  );
  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.key, FILMS.filter(f.test).length])),
    [],
  );

  // The card pages through the films currently on the wall. A deep-linked film
  // hidden by the filter still opens; it just pages through the full list.
  const deck = films.some((f) => f.id === openId) ? films : order;
  const openIndex = deck.findIndex((f) => f.id === openId);
  const openFilm = openIndex >= 0 ? deck[openIndex] : null;

  const openCard = useCallback((film, el) => {
    openerRef.current = el ?? null;
    setHovered(null);
    setOpenId(film.id);
    setFilmInUrl(film.id);
  }, []);

  const step = useCallback((dir) => {
    setOpenId((id) => {
      const i = deck.findIndex((f) => f.id === id);
      const next = deck[(i + dir + deck.length) % deck.length];
      setFilmInUrl(next.id);
      return next.id;
    });
  }, [deck]);

  const closeCard = useCallback(() => {
    const id = openId;
    setOpenId(null);
    setFilmInUrl(null);
    // hand focus back to the film that is now showing, or the one that opened it
    const target = document.querySelector(`[data-film="${id}"]:not([data-clone])`) ?? openerRef.current;
    target?.focus({ preventScroll: true });
  }, [openId]);

  // Where the sheet should grow from / shrink back to: the tile of that film
  // nearest the middle of the screen, if one is actually visible.
  const getOrigin = useCallback((id) => {
    const vw = window.innerWidth, vh = window.innerHeight;
    let best = null, bestD = Infinity;
    document.querySelectorAll(`[data-film="${id}"]`).forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.right < 0 || r.bottom < 0 || r.left > vw || r.top > vh || r.width < 4) return;
      const d = Math.hypot(r.left + r.width / 2 - vw / 2, r.top + r.height / 2 - vh / 2);
      if (d < bestD) { bestD = d; best = r; }
    });
    return best;
  }, []);

  const changeFilter = (key) => {
    if (key === filter) return;
    setFilter(key);
    setWallKey((k) => k + 1);
  };
  const reshuffle = () => {
    setOrder((o) => shuffle(o));
    setWallKey((k) => k + 1);
  };

  useEffect(() => {
    document.title = openFilm ? `${openFilm.title} — ${openFilm.sub} · Drew Hallé` : 'Videography · Drew Hallé';
  }, [openFilm]);

  const caption = hovered;

  return (
    <>
      <TopNav />

      <main className={`${styles.page} ${flat ? styles.pageFlat : ''}`}>
        <h1 className={styles.srOnly}>Videography</h1>

        {flat ? (
          <FilmGrid key={wallKey} films={films} onOpen={openCard} />
        ) : (
          <FilmWall key={wallKey} films={films} onOpen={openCard} onHover={setHovered} frozen={!!openFilm} />
        )}

        <div className={styles.filters} role="group" aria-label="Filter films">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`${styles.filter} ${filter === f.key ? styles.filterOn : ''}`}
              aria-pressed={filter === f.key}
              onClick={() => changeFilter(f.key)}
            >
              {f.label}<sup>{counts[f.key]}</sup>
            </button>
          ))}
        </div>

        {!flat && (
          <>
            <button type="button" className={styles.shuffle} onClick={reshuffle} aria-label="Shuffle the wall">
              <IconShuffle />
              <span>shuffle</span>
            </button>

            <div className={styles.caption} aria-hidden="true">
              {caption ? (
                <div key={caption.id} className={styles.captionIn}>
                  <p className={styles.captionTitle}>{caption.title}</p>
                  <p className={styles.captionMeta}>
                    {caption.sub}
                    {caption.year && <span> · {caption.year}</span>}
                    <span> · {caption.aspect}</span>
                    {caption.duration != null && <span> · {formatRuntime(caption.duration)}</span>}
                  </p>
                </div>
              ) : (
                <div key="idle" className={styles.captionIn}>
                  <p className={styles.captionTitle}>Videography</p>
                  <p className={styles.captionMeta}>{films.length} films · move to explore · click to watch</p>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {openFilm && (
        <FilmCard
          key="film-card"
          film={openFilm}
          index={openIndex}
          total={deck.length}
          getOrigin={getOrigin}
          onClose={closeCard}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          reduced={reduced}
        />
      )}
    </>
  );
}
